import { CompositeFilterDescriptor, GroupDescriptor, process, State } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IScenarioPreprogramme } from './ScenarioPreprogramme';
import { EditEvent, GridComponent, RemoveEvent } from '@progress/kendo-angular-grid';
import { BaseObject } from './BaseObject';
import { BaseObjectFactory } from './BaseObjectFactory';

export interface GridDataResult<T> {
  /**
   * The data that will be rendered by the Grid as an array.
   */
  data: T[];
  /**
   * The total number of records that are available.
   */
  total: number;
}

export class GridWrapper<T extends BaseObject> {
  private readonly SKIP = undefined;
  private readonly TAKE = undefined;

  private data!: Array<T>;
  private storeSubscription!: Subscription;

  public loading = true;
  public editedRowIndex: number | undefined;
  public gridData!: GridDataResult<T>;
  public gridState!: State;
  public mySelection: Array<number | string> = [];
  public readonly dataUpdated = new BehaviorSubject<number>(0);

  get processedData(): T[] {
    return this.gridData.data.map((item) => BaseObjectFactory.create(this.className, item) as T);
  }

  get internalData(): T[] {
    return this.data;
  }

  constructor(
    private className: string,
    private observable: Observable<Array<T> | undefined>,
    private filter: CompositeFilterDescriptor | undefined,
    private readonly kendoGrid: GridComponent,
    private readonly aggregates: any[] = [],
    private callBackEditHandler?: (item: EditEvent) => void,
    private selectedItem?: T
  ) {
    this.gridState = {
      skip: this.SKIP,
      take: this.TAKE,
      sort: [],
      group: [],
    };
    this.storeSubscription = this.observable.subscribe(this.reloadGrid.bind(this));
  }

  public reload(observable: Observable<Array<T> | undefined>): void {
    this.loading = true;
    this.storeSubscription.unsubscribe();
    this.observable = observable;
    this.storeSubscription = this.observable.subscribe(this.reloadGrid.bind(this));
  }

  private reloadGrid(data: T[] | undefined): void {
    if (data == null) {
      return;
    }

    this.data = data || [];
    this.updateGrid();
    if (this.selectedItem && this.selectedItem.id) {
      this.mySelection = [this.selectedItem.id];
    }

    this.dataUpdated.next(this.data.length);

    this.loading = false;
  }

  public refresh(): void {
    this.updateGrid();
  }

  public add(item: T): void {
    this.data.push(item);
    this.updateGrid();
  }

  public addMany(item: T[]): void {
    this.data.push(...item);
    this.updateGrid();
  }

  public cancelChanges(grid: any): void {
    grid.cancelCell();
  }

  public onDataStateChange(state: State): void {
    if (state && state.group) {
      state.group.map((group) => (group.aggregates = this.aggregates));
    }
    this.gridState = state;
    this.updateGrid();
  }

  public addHandler(): void {
    const item: EditEvent = {
      // @ts-ignore
      sender: null,
      rowIndex: -1,
      isNew: true,
      dataItem: BaseObjectFactory.create(this.className, null) as T,
    };
    this.editHandler(item);
  }

  public editHandler(item: EditEvent): void {
    if (this.callBackEditHandler) {
      this.callBackEditHandler(item);
    }
  }

  public saveHandler({ sender, formGroup, rowIndex }: any): void {
    if (formGroup.valid) {
      if (this.editedRowIndex === rowIndex) {
        for (let i = 0; i < this.data.length; i++) {
          if (this.data[i].uuid === formGroup.value.uuid) {
            this.data[i] = BaseObjectFactory.create(this.className, formGroup.value) as T;
            this.editedRowIndex = undefined;
            break;
          }
        }
      } else {
        const item = BaseObjectFactory.create(this.className, formGroup.value) as T;
        this.data.push(item);
      }

      sender.closeRow(rowIndex);
      this.updateGrid();
    }
  }

  public cancelHandler({ sender, rowIndex }: any): void {
    sender.closeRow(rowIndex);
  }

  public removeHandler(event: RemoveEvent): void {
    const idx = this.data.findIndex((x) => x.uuid === event.dataItem.uuid);
    this.data.splice(idx, 1);

    event.sender.cancelCell();
    this.updateGrid();
  }

  public moveUp(item: BaseObject): void {
    const index = this.gridData.data.findIndex((x) => x.uuid === item.uuid);
    moveItemInArray(this.gridData.data, index, index - 1);
  }

  public moveDown(item: BaseObject): void {
    const index = this.gridData.data.findIndex((x) => x.uuid === item.uuid);
    moveItemInArray(this.gridData.data, index, index + 1);
  }

  public drop(event: CdkDragDrop<IScenarioPreprogramme>): void {
    moveItemInArray(this.gridData.data, event.previousIndex, event.currentIndex);
    this.updateGrid();
  }

  public onFilter(eventTarget: EventTarget | null): void {
    if (!eventTarget) {
      return;
    }
    const inputValue: string = (eventTarget as HTMLInputElement).value;

    if (this.filter) {
      this.filter.filters.forEach((x) => {
        // @ts-ignore
        x.value = inputValue;
      });
    }

    this.gridState.take = this.data.length;
    this.gridState.skip = this.SKIP;
    this.gridState.filter = Object.assign({}, this.filter);

    this.updateGrid();
  }

  public clearFilter(): void {
    if (this.filter) {
      this.filter.filters.forEach((x) => {
        // @ts-ignore
        x.value = null;
      });
    }

    this.gridState.take = this.TAKE;
    this.gridState.skip = this.SKIP;
    this.gridState.filter = { filters: [], logic: 'or' };
    this.updateGrid();
  }

  public groupChanged(groups: Array<GroupDescriptor> | undefined): void {
    for (const column of this.kendoGrid.columns) {
      column.hidden = false;

      if (groups) {
        if (groups.length === 0) {
          column.hidden = false;
        } else {
          for (const group of groups) {
            // @ts-ignore
            if (group.field === column.field) {
              column.hidden = true;
            }
          }
        }
      }
    }
  }

  public destroy(): void {
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    if (this.dataUpdated) {
      this.dataUpdated.unsubscribe();
    }
  }

  private updateGrid(): void {
    this.gridData = process(this.data, this.gridState);
  }
}
